import { supabase } from "@/integrations/supabase/client";
import { Board, Sprint, Project, Task } from "./boardTypes";
import { TaskRole } from "@/components/scheduler/schedulerTypes";
import { DEFAULT_COLUMNS, DEFAULT_COLUMN_ORDER } from "./boardConstants";

// Custom hook in boardService is removed as we'll use useUser directly

// For non-component code, pass companyId as a parameter
export const fetchBoards = async (companyId: number | null): Promise<{boards: Board[], projects: Project[]}> => {
  if (!companyId) return { boards: [], projects: [] };
  
  try {
    const { data: clients, error: clientsError } = await supabase
      .from('clients')
      .select('id')
      .eq('company_id', companyId);
      
    if (clientsError) throw clientsError;
    
    if (!clients || clients.length === 0) return { boards: [], projects: [] };
    
    const clientIds = clients.map(client => client.id);
    
    const { data: projects, error: projectsError } = await supabase
      .from('projects')
      .select('id, name')
      .in('client_id', clientIds);
      
    if (projectsError) throw projectsError;
    
    if (!projects || projects.length === 0) return { boards: [], projects: [] };
    
    const projectIds = projects.map(project => project.id);
    
    const { data: boardsData, error: boardsError } = await supabase
      .from('boards')
      .select('*')
      .in('project_id', projectIds);
      
    if (boardsError) throw boardsError;
    
    // Store the projects map for board name lookup
    const projectsMap = projects.reduce((acc, project) => {
      acc[project.id] = project;
      return acc;
    }, {} as Record<number, { id: number; name: string }>);
    
    // Attach project names to the boards for easier reference
    const boardsWithProjects = (boardsData || []).map(board => ({
      ...board,
      project_name: projectsMap[board.project_id]?.name || 'Board'
    }));
    
    return { boards: boardsWithProjects, projects };
  } catch (error) {
    console.error('Error fetching boards:', error);
    throw error;
  }
};

export const createDefaultBoard = async (projectId: number): Promise<Board | null> => {
  try {
    const { data, error } = await supabase
      .from('boards')
      .insert([{ project_id: projectId }])
      .select();
      
    if (error) throw error;
    
    if (data && data.length > 0) {
      return data[0];
    }
    return null;
  } catch (error) {
    console.error('Error creating default board:', error);
    throw error;
  }
};

export const createBacklogSprint = async (boardId: string): Promise<Sprint | null> => {
  try {
    // Create a backlog sprint with infinite dates (far in the past to far in the future)
    const pastDate = new Date(2000, 0, 1); // January 1, 2000
    const futureDate = new Date(2100, 11, 31); // December 31, 2100
    
    const { data, error } = await supabase
      .from('sprints')
      .insert([
        { 
          title: 'Backlog',
          start_date: pastDate.toISOString().split('T')[0],
          end_date: futureDate.toISOString().split('T')[0],
          status: 'backlog',
          board_id: boardId
        }
      ])
      .select();
      
    if (error) throw error;
    
    if (data && data.length > 0) {
      return data[0];
    }
    return null;
  } catch (error) {
    console.error('Error creating backlog sprint:', error);
    throw error;
  }
};

export const ensureBoardHasBacklog = async (boardId: string): Promise<void> => {
  try {
    // Check if a backlog sprint exists for this board
    const { data: existingBacklog, error } = await supabase
      .from('sprints')
      .select('*')
      .eq('board_id', boardId)
      .eq('status', 'backlog')
      .maybeSingle();
      
    if (error) throw error;
    
    // If no backlog exists, create one
    if (!existingBacklog) {
      console.log('No backlog found for board', boardId, 'creating one...');
      await createBacklogSprint(boardId);
    }
  } catch (error) {
    console.error('Error checking for backlog sprint:', error);
    throw error;
  }
};

export const fetchProjectDetails = async (boardId: string | null): Promise<Project | null> => {
  if (!boardId) return null;
  
  try {
    const { data: boardData, error: boardError } = await supabase
      .from('boards')
      .select('project_id')
      .eq('id', boardId)
      .single();
      
    if (boardError) throw boardError;
    
    if (boardData && boardData.project_id) {
      const { data: projectData, error: projectError } = await supabase
        .from('projects')
        .select('id, name')
        .eq('id', boardData.project_id)
        .single();
        
      if (projectError) throw projectError;
      
      if (projectData) {
        return projectData;
      }
    }
    return null;
  } catch (error) {
    console.error('Error fetching project details:', error);
    throw error;
  }
};

export const fetchSprint = async (sprintId: string): Promise<Sprint | null> => {
  try {
    const { data, error } = await supabase
      .from('sprints')
      .select('id, title, start_date, end_date, status, board_id')
      .eq('id', sprintId)
      .single();
      
    if (error) throw error;
    
    if (data) {
      return data;
    }
    return null;
  } catch (error) {
    console.error('Error fetching sprint details:', error);
    throw error;
  }
};

export const fetchAllSprints = async (boardId: string | null): Promise<Sprint[]> => {
  if (!boardId) return [];
  
  try {
    const { data, error } = await supabase
      .from('sprints')
      .select('id, title, start_date, end_date, status, board_id')
      .eq('board_id', boardId)
      .order('start_date', { ascending: true });
      
    if (error) throw error;
    
    return data || [];
  } catch (error) {
    console.error('Error fetching all sprints:', error);
    throw error;
  }
};

export const fetchTasks = async (boardId: string | null, selectedSprintId: string | 'all'): Promise<{
  tasks: { [key: string]: Task };
  columns: { [key: string]: { id: string; title: string; taskIds: string[] } };
  columnOrder: string[];
}> => {
  if (!boardId) {
    return {
      tasks: {},
      columns: DEFAULT_COLUMNS,
      columnOrder: DEFAULT_COLUMN_ORDER
    };
  }
  
  try {
    let sprintsQuery = supabase
      .from('sprints')
      .select('id')
      .eq('board_id', boardId);
    
    const { data: sprintsData, error: sprintsError } = await sprintsQuery;
    if (sprintsError) throw sprintsError;
    
    if (sprintsData && sprintsData.length > 0) {
      const sprintIds = sprintsData.map(sprint => sprint.id);
      
      // First, fetch the tasks and order by index for consistent ordering
      let query = supabase
        .from('tasks')
        .select('*')
        .in('sprint_id', sprintIds)
        .order('status', { ascending: true })
        .order('index', { ascending: true }); // Order by index within each status
      
      if (selectedSprintId !== 'all') {
        query = query.eq('sprint_id', selectedSprintId);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      const tasks: { [key: string]: Task } = {};
      const columnTaskIds: { [key: string]: string[] } = {
        'column-1': [], // planned
        'column-2': [], // in_progress
        'column-3': [], // testing
        'column-4': [], // completed
      };
      
      // Get all unique employee IDs
      const employeeIds: number[] = [];
      if (data) {
        data.forEach(task => {
          if (task.employee_id && !employeeIds.includes(task.employee_id)) {
            employeeIds.push(task.employee_id);
          }
        });
      }
      
      // Fetch employee names in a single query if we have employee IDs
      const employeeNameMap: Record<number, string> = {};
      if (employeeIds.length > 0) {
        const { data: employees, error: employeesError } = await supabase
          .from('employees')
          .select('id, name')
          .in('id', employeeIds);
          
        if (employeesError) {
          console.error("Error fetching employee names:", employeesError);
        } else if (employees) {
          employees.forEach(employee => {
            employeeNameMap[employee.id] = employee.name;
          });
        }
      }
      
      if (data) {
        data.forEach(task => {
          const taskId = task.id;
          tasks[taskId] = {
            id: taskId,
            title: task.title, // Add title from the database
            content: task.title, // Set content to match title
            description: task.description,
            role: task.role,
            sprint_id: task.sprint_id,
            employee_id: task.employee_id,
            employee_name: task.employee_id ? employeeNameMap[task.employee_id] || null : null,
            status: task.status, // Add status from the database
            index: task.index // Include index in the task object
          };
          
          switch (task.status) {
            case 'planned':
              columnTaskIds['column-1'].push(taskId);
              break;
            case 'in_progress':
              columnTaskIds['column-2'].push(taskId);
              break;
            case 'testing':
              columnTaskIds['column-3'].push(taskId);
              break;
            case 'completed':
              columnTaskIds['column-4'].push(taskId);
              break;
            default:
              columnTaskIds['column-1'].push(taskId);
          }
        });
      }
      
      const updatedColumns = { ...DEFAULT_COLUMNS };
      Object.keys(updatedColumns).forEach(columnId => {
        updatedColumns[columnId] = {
          ...updatedColumns[columnId],
          taskIds: columnTaskIds[columnId] || []
        };
      });
      
      return {
        tasks,
        columns: updatedColumns,
        columnOrder: DEFAULT_COLUMN_ORDER
      };
    } else {
      return {
        tasks: {},
        columns: DEFAULT_COLUMNS,
        columnOrder: DEFAULT_COLUMN_ORDER
      };
    }
  } catch (error) {
    console.error("Error fetching tasks:", error);
    throw error;
  }
};

export const createTask = async (
  title: string, 
  description: string, 
  role: TaskRole, 
  sprintId: string, 
  employeeId?: number
): Promise<Task | null> => {
  try {
    // Get the max index for the planned status to place the new task at the end
    const index = await getMaxIndexForStatus(sprintId, 'planned');
    
    const { data, error } = await supabase
      .from('tasks')
      .insert([
        { 
          title,
          description,
          role,
          status: 'planned',
          sprint_id: sprintId,
          employee_id: employeeId || null,
          index // Set the index for the new task
        }
      ])
      .select();
      
    if (error) throw error;
    
    if (data && data.length > 0) {
      return {
        id: data[0].id,
        title: data[0].title, // Add title
        content: data[0].title, // Set content to match title
        description: data[0].description,
        role: data[0].role,
        sprint_id: data[0].sprint_id,
        employee_id: data[0].employee_id,
        status: data[0].status,
        index: data[0].index
      };
    }
    return null;
  } catch (error) {
    console.error("Error creating task:", error);
    throw error;
  }
};

export const updateTask = async (
  taskId: string,
  title: string, 
  description: string, 
  role: TaskRole, 
  sprintId: string, 
  employeeId?: number
): Promise<Task | null> => {
  try {
    // First, get the current task to preserve its index
    const { data: currentTask, error: fetchError } = await supabase
      .from('tasks')
      .select('index, status')
      .eq('id', taskId)
      .single();
      
    if (fetchError) throw fetchError;
    
    // Preserve the current index and status in the update
    const { data, error } = await supabase
      .from('tasks')
      .update({ 
        title,
        description,
        role,
        sprint_id: sprintId,
        employee_id: employeeId || null,
        // Keep the same index and status to maintain position
        index: currentTask?.index || 1000,
        status: currentTask?.status || 'planned'
      })
      .eq('id', taskId)
      .select();
      
    if (error) throw error;
    
    if (data && data.length > 0) {
      return {
        id: data[0].id,
        title: data[0].title, // Add title
        content: data[0].title, // Set content to match title
        description: data[0].description,
        role: data[0].role,
        sprint_id: data[0].sprint_id,
        employee_id: data[0].employee_id,
        status: data[0].status,
        index: data[0].index
      };
    }
    return null;
  } catch (error) {
    console.error("Error updating task:", error);
    throw error;
  }
};

export const createSprint = async (
  title: string, 
  startDate: Date, 
  endDate: Date, 
  status: string, 
  boardId: string | null
): Promise<Sprint | null> => {
  try {
    const { data, error } = await supabase
      .from('sprints')
      .insert([
        { 
          title,
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0],
          status: status as 'planned' | 'in_progress' | 'completed',
          board_id: boardId
        }
      ])
      .select();
      
    if (error) throw error;
    
    if (data && data.length > 0) {
      return data[0];
    }
    return null;
  } catch (error) {
    console.error("Error creating sprint:", error);
    throw error;
  }
};

export const deleteTask = async (taskId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('tasks')
      .delete()
      .eq('id', taskId);
      
    if (error) throw error;
    
    return true;
  } catch (error) {
    console.error("Error deleting task:", error);
    throw error;
  }
};

export const moveTasks = async (fromSprintId: string, toSprintId: string): Promise<boolean> => {
  try {
    // Find all tasks from the source sprint
    const { data: tasksData, error: tasksError } = await supabase
      .from('tasks')
      .select('id')
      .eq('sprint_id', fromSprintId);
      
    if (tasksError) throw tasksError;
    
    // If there are tasks, update their sprint_id
    if (tasksData && tasksData.length > 0) {
      const taskIds = tasksData.map(task => task.id);
      
      const { error: updateError } = await supabase
        .from('tasks')
        .update({ sprint_id: toSprintId })
        .in('id', taskIds);
        
      if (updateError) throw updateError;
    }
    
    return true;
  } catch (error) {
    console.error("Error moving tasks between sprints:", error);
    throw error;
  }
};

export const deleteSprint = async (
  sprintId: string, 
  options: { 
    deleteTasks: boolean, 
    targetSprintId?: string 
  }
): Promise<boolean> => {
  try {
    // If we're not deleting tasks, move them to the target sprint
    if (!options.deleteTasks && options.targetSprintId) {
      await moveTasks(sprintId, options.targetSprintId);
    }
    // If we're deleting tasks, delete them first
    else if (options.deleteTasks) {
      const { data: tasksData, error: tasksError } = await supabase
        .from('tasks')
        .select('id')
        .eq('sprint_id', sprintId);
        
      if (tasksError) throw tasksError;
      
      if (tasksData && tasksData.length > 0) {
        const taskIds = tasksData.map(task => task.id);
        const { error: deleteTasksError } = await supabase
          .from('tasks')
          .delete()
          .in('id', taskIds);
          
        if (deleteTasksError) throw deleteTasksError;
      }
    }
    
    // Now delete the sprint
    const { error } = await supabase
      .from('sprints')
      .delete()
      .eq('id', sprintId);
      
    if (error) throw error;
    
    return true;
  } catch (error) {
    console.error("Error deleting sprint:", error);
    throw error;
  }
};

export const updateTaskStatusAndIndex = async (
  taskId: string,
  status: "planned" | "in_progress" | "testing" | "completed",
  index: number
) => {
  try {
    const { error } = await supabase
      .from('tasks')
      .update({
        status,
        index,
        updated_at: new Date().toISOString(),
      })
      .eq('id', taskId);

    if (error) throw error;
  } catch (error) {
    console.error('Error updating task status and index:', error);
    throw error;
  }
}

/**
 * Efficiently updates multiple tasks' positions in a single batch operation
 * This reduces the number of database calls when reordering multiple tasks
 */
export const batchUpdateTaskPositions = async (
  tasks: { id: string, status: 'planned' | 'in_progress' | 'testing' | 'completed', index: number }[]
): Promise<boolean> => {
  try {
    // Update each task individually to avoid upsert issues
    for (const task of tasks) {
      const { error } = await supabase
        .from('tasks')
        .update({
          status: task.status,
          index: task.index
        })
        .eq('id', task.id);
        
      if (error) throw error;
    }
    
    return true;
  } catch (error) {
    console.error("Error batch updating task positions:", error);
    throw error;
  }
};

/**
 * Returns the maximum index value for tasks in a specific status column
 * Used when adding new tasks to ensure they're appended to the end
 */
export const getMaxIndexForStatus = async (
  sprintId: string,
  status: "planned" | "in_progress" | "testing" | "completed"
): Promise<number> => {
  try {
    const { data, error } = await supabase
      .from('tasks')
      .select('index')
      .eq('sprint_id', sprintId)
      .eq('status', status)
      .order('index', { ascending: false })
      .limit(1);
      
    if (error) throw error;
    
    if (data && data.length > 0 && data[0].index !== null) {
      return data[0].index + 1000; // Return next position with gap for future insertions
    }
    
    return 1000; // Default starting position if no tasks exist
  } catch (error) {
    console.error("Error getting max index for status:", error);
    throw error;
  }
};
